import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiBaseUrl, getAuthorizationToken, getCheckoutDetails, getTokens } from "../api-helper";
import { useAccountContext, useAccountContextDispatch } from "../context/AccountContext";

export default function PaymentStatus() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isValid, setIsValid] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingDetails, setIsFetchingDetails] = useState(false);
    const [paymentData, setPaymentData] = useState(null);
    const [error, setError] = useState(null);

    const retrievePaymentDetails = useCallback(async(cs) => {
        setIsFetchingDetails(true);
        setError(null);
        try {
            const response = await getCheckoutDetails(cs);
            setPaymentData(response);
        } catch (err) {
            console.error('[PaymentStatus] Error fetching payment details:', err);
            setError(err.response?.message || 'Failed to retrieve payment details');
        } finally {
            setIsFetchingDetails(false);
        }
    }, []);

    useEffect(() => {
        console.log('[PaymentStatus] useEffect triggered, searchParams:', searchParams.toString());

        // Try to get cs from searchParams first, then fallback to parsing hash manually
        let cs = searchParams.get('cs');

        // Fallback: parse from hash for HashRouter (handles external redirects)
        if (!cs && window.location.hash) {
            const hashParts = window.location.hash.split('?');
            if (hashParts.length > 1) {
                const hashParams = new URLSearchParams(hashParts[1]);
                cs = hashParams.get('cs');
                console.log('[PaymentStatus] Parsed cs from hash:', cs);
            }
        }

        if (!cs || cs.trim() === '') {
            console.log('[PaymentStatus] No valid cs parameter found, redirecting...');
            navigate('/');
            return;
        }

        console.log('[PaymentStatus] Valid session ID found:', cs);
        setIsValid(true);
        setIsLoading(false);

        retrievePaymentDetails(cs);

    }, [searchParams, navigate, retrievePaymentDetails]);

    if (isLoading) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <h1 className="bg-dark-blue/30 text-4xl p-5">Validating payment...</h1>
            </div>
        );
    }

    if (!isValid) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <h1 className="bg-dark-blue/30 text-4xl p-5">Invalid payment session</h1>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col items-center justify-center">
            {error && (
                <div className="bg-red-500/30 text-red-200 p-4 rounded-lg mb-4 max-w-md w-full text-center">
                    {error}
                </div>
            )}
            {isFetchingDetails ? (
                <h1 className="bg-dark-blue/30 text-4xl p-5">Loading payment details...</h1>
            ) : (
                <h1 className="bg-dark-blue/40 text-6xl p-5 rounded-lg">Payment Confirmation</h1>
            )}
            {paymentData?.purchase && (
                <>
                    <div className="bg-dark-blue/40 p-6 mt-4 rounded-lg max-w-md w-full">
                        <div className="space-y-3">
                            <div className="flex justify-between items-center border-b border-white/20 pb-2">
                                <span className="text-white">Product</span>
                                <span className="font-semibold text-yellow">{paymentData.purchase.productName}</span>
                            </div>
                            {paymentData.purchase.productDescription && paymentData.purchase.productDescription !== paymentData.purchase.productName && (
                                <div className="flex justify-between items-center border-b border-white/20 pb-2">
                                    <span className="text-white">Description</span>
                                    <span className="text-yellow">{paymentData.purchase.productDescription}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center border-b border-white/20 pb-2">
                                <span className="text-white">Amount</span>
                                <span className="font-semibold text-lg text-yellow">{paymentData.purchase.amount}</span>
                            </div>
                            {paymentData.purchase.currency && (
                                <div className="flex justify-between items-center border-b border-white/20 pb-2">
                                    <span className="text-white">Currency</span>
                                    <span className="text-yellow">{paymentData.purchase.currency}</span>
                                </div>
                            )}
                            {paymentData.purchase.trialStart && (
                                <div className="flex justify-between items-center border-b border-white/20 pb-2">
                                    <span className="text-white">Trial Start</span>
                                    <span className="text-yellow">{paymentData.purchase.trialStart}</span>
                                </div>
                            )}
                            {paymentData.purchase.trialEnd && (
                                <div className="flex justify-between items-center border-b border-white/20 pb-2">
                                    <span className="text-white">Trial End</span>
                                    <span className="text-yellow">{paymentData.purchase.trialEnd}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center">
                                <span className="text-white">Status</span>
                                <span className={`font-semibold ${paymentData.purchase.status === 'Succeeded' || paymentData.purchase.status === 'Active' ? 'text-green-400' : 'text-yellow-400'}`}>
                                    {paymentData.purchase.status}
                                </span>
                            </div>

                        </div>
                    </div>
                    {paymentData.purchase.downloadLink && (
                        <a
                            href={paymentData.purchase.downloadLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 px-8 py-4 bg-yellow hover:brightness-110 text-dark-blue text-xl font-bold rounded-lg shadow-lg transition-all duration-200 hover:scale-105"
                        >
                            Download Promptus App
                        </a>
                    )}
                </>
            )}
            {!isFetchingDetails && !paymentData?.purchase && !error && (
                <p className="text-white/60 mt-4">No payment data available</p>
            )}
        </div>
    );
} 